import { useEffect, useRef, useState } from 'react';

import { CfsApi } from '@/api';
import { ICallerLocation, ICFS } from '@/models';
import { useUserStore } from '@/store';

export const useRapidSos = (cfs: ICFS | null) => {
  const { account } = useUserStore();
  const [locations, setLocations] = useState<ICallerLocation[]>([]);
  const [additionalData, setAdditionalData] = useState<any>(null);
  const isNotFound = useRef(false);
  const isAdditionalDataFound = useRef(false);
  const [isDifferingReporter, setIsDifferingReporter] = useState(true);

  useEffect(() => {
    if (!cfs) {
      return;
    }

    if (!cfs?.primaryReporter?.phoneNumbers) {
      setIsDifferingReporter(false);
      return;
    }

    const matching = cfs?.primaryReporter?.phoneNumbers?.find((pn) => {
      if (pn.phoneNumber === cfs.reporter?.callerPhoneNumber) {
        return true;
      }
    });
    if (!matching) {
      setIsDifferingReporter(true);
      setAdditionalData(null);
      setLocations([]);
    } else {
      setIsDifferingReporter(false);
    }
  }, [cfs, cfs?.primaryReporter]);

  const fetchCallerLocations = async () => {
    if (!cfs?._id || isNotFound.current) {
      return;
    }

    // Do not fetch rapidsos data for differing reporters unless the flag is
    // set per account basis.
    if (
      !isDifferingReporter ||
      account?.settings?.features?.isEnabledRapidSOSForDifferingReporter
    ) {
      try {
        const apiResult = await CfsApi.getCfsCallerLocations(cfs._id);
        if (
          apiResult.status === 200 &&
          apiResult.data &&
          apiResult.data.length > 0
        ) {
          const newLocations = apiResult.data;

          setLocations((currentLocations) => {
            if (currentLocations.length === 0) {
              return newLocations;
            } else if (
              currentLocations[currentLocations.length - 1].created_time
            ) {
              const lastNewLocation = newLocations[newLocations.length - 1];
              const lastLocation =
                currentLocations[currentLocations.length - 1];

              if (lastLocation.created_time !== lastNewLocation.created_time) {
                return [...currentLocations, ...apiResult.data];
              }
            }

            return currentLocations;
          });
        }
      } catch (err: any) {
        if (err.status === 404) {
          isNotFound.current = true;
        }
      }
    }
  };

  const fetchAdditionalData = async () => {
    if (!cfs?._id || isAdditionalDataFound.current) {
      return;
    }

    // Do not fetch rapidsos data for differing reporters unless the flag is
    // set per account basis.
    if (
      !isDifferingReporter ||
      account?.settings?.features?.isEnabledRapidSOSForDifferingReporter
    ) {
      try {
        const apiResult = await CfsApi.getAdditionalData(cfs._id);
        if (apiResult.status === 200 && apiResult.data) {
          isAdditionalDataFound.current = true;
          setAdditionalData(apiResult.data);
        }
      } catch (err: any) {
        if (err.status === 404) {
          isAdditionalDataFound.current = true;
        }
      }
    }
  };

  useEffect(() => {
    if (cfs?.reporter?.callerPhoneNumber && !cfs.isClosed) {
      const t = setInterval(() => fetchCallerLocations(), 5000);
      fetchAdditionalData();
      const t2 = setInterval(() => fetchAdditionalData(), 15000);
      return () => {
        clearInterval(t);
        clearInterval(t2);
      };
    }
  }, [
    cfs?.reporter?.callerPhoneNumber,
    cfs?.isClosed,
    isDifferingReporter,
    cfs?.primaryReporter,
  ]);

  if (cfs === null) {
    return {};
  }

  return {
    locations,
    additionalData,
  };
};
